import { useState, useCallback, useRef, useEffect } from 'react';

import { AxiosError } from 'axios';

// Hook for modifying data via the API (POST, PUT, DELETE requests).
// Uses:
//   Basic mutate:
//   1. Call the `mutate()` method to submit changes to the backend.
//   2. You can optionally initialize the hook with the desired params or provide them when calling `mutate(params)`
//   3. Use `data`, `loading`, and `error` values to render desired UX.

// Silly alias, but decouples error handling a little from axios throughout our codebase.
export type MutateError = AxiosError;

type MutateDataFn<P, D> = (params: P | undefined) => Promise<D>;

type Options<D> = {
  onCompleted?: (data: D | {}) => void;
  onError?: (error: MutateError) => void;
};

type ReturnValues<P, D> = [
  (params?: P) => Promise<D | undefined>,
  D | undefined,
  boolean,
  MutateError | undefined,
  boolean,
];

export function useMutate<P, D>(mutateDataFn: MutateDataFn<P, D>, options: Options<D> = {}, params?: P): ReturnValues<P, D> {
  const [loading, setLoading] = useState(false);
  const [called, setCalled] = useState(false);
  const [data, setData] = useState<D>();
  const [error, setError] = useState<MutateError>();

  const isMountedRef = useRef(true);

  const mutateData = async (providedParams?: P) => {
    try {
      setLoading(true);
      const resData = await mutateDataFn(providedParams || params);

      if (isMountedRef.current) {
        setData(resData);
        options.onCompleted?.(resData);
      }

      return resData;
    } catch (err: any) {
      // If the error didn't come from the backend, don't swallow it.
      if (err.response) {
        if (isMountedRef.current) {
          setError(err);
          options.onError?.(err);
        }
      } else {
        throw err;
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setCalled(true);
      }
    }
  };

  // Memoize the handlers so they maintain the same reference between renders:
  const mutate = useCallback(mutateData, [JSON.stringify(params)]);
  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  return [mutate, data, loading, error, called];
}
